import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  List,
  ListItem,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { IOrder } from "../../../types/orders";
import OrderItem from "../OrderItem";
import { sortBy } from "lodash";
import StatusIcon, { STATUSES } from "../../../components/StatusIcon";
import { getTotal, getTotalCost } from "../utils";
import useUpdateOrderStatus from "../../../services/useUpdateOrderStatus";
import AddItem from "../AddItem";

interface Props {
  checked: boolean;
  fairId: number;
  forceExpand: boolean;
  order: IOrder;
  toggle: () => void;
  showCosts: boolean;
}

const Order: FC<Props> = ({
  fairId,
  forceExpand,
  order,
  checked,
  toggle,
  showCosts,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const { mutate } = useUpdateOrderStatus();

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const setStatus = (status: keyof typeof STATUSES) => {
    if (status === "CANCELED" && order.status === "PAID") {
      setOpen(true);
    } else {
      mutate({ orderId: order.id, status });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCancel = (createCredit: boolean) => () => {
    setOpen(false);
    mutate({ createCredit, orderId: order.id, status: "CANCELED" });
  };

  const total = getTotal(order);
  const totalCost = getTotalCost(order);
  // Usa o status do Order diretamente (não mais calculado dos items)
  const status = order.status as keyof typeof STATUSES;

  // Transições de status permitidas para Orders
  let allowedStatuses: (keyof typeof STATUSES)[] = [];
  if (status === "PENDING") {
    allowedStatuses = ["PAID", "CANCELED"];
  } else if (status === "PAID") {
    allowedStatuses = ["DELIVERED", "CANCELED"];
  } else if (status === "DELIVERED") {
    allowedStatuses = []; // Delivered é final
  } else if (status === "CANCELED") {
    allowedStatuses = []; // Canceled é final
  }

  return (
    <>
      <ListItem disablePadding>
        <Checkbox checked={checked} onChange={toggle} />
        <StatusIcon
          setStatus={setStatus}
          status={status}
          statuses={allowedStatuses}
        />
        <AddItem fairId={fairId} orderId={order.id} />
        <ListItemButton onClick={handleToggle}>
          <Typography sx={{ flex: "1 1 auto" }}>
            {order.urban_partner.name}
          </Typography>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded || forceExpand}>
        <List component="div" disablePadding>
          <ListItem
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {order.delivery_method_title && (
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  p: 1,
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  borderRadius: 1,
                  width: "100%",
                }}
              >
                <strong>Método de entrega:</strong>{" "}
                {order.delivery_method_title}
                {order.delivery_method_description && (
                  <span> - {order.delivery_method_description}</span>
                )}{" "}
                (
                {order.delivery_method_is_home_delivery
                  ? "Entrega em casa"
                  : "Retirada"}
                )
              </Typography>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Qtd.</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell
                      align="right"
                      sx={!showCosts ? { visibility: "hidden" } : {}}
                    >
                      Custo
                    </TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortBy(order.items, "product.base_product.name").map(
                    (item) => (
                      <OrderItem
                        fairId={fairId}
                        key={item.id}
                        item={item}
                        orderStatus={order.status}
                        showCost={showCosts}
                      />
                    ),
                  )}
                  {parseFloat(order.delivery_fee) > 0 &&
                  status !== "CANCELED" ? (
                    <TableRow>
                      <TableCell align="right" colSpan={3}>
                        Frete
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={!showCosts ? { visibility: "hidden" } : {}}
                      ></TableCell>
                      <TableCell align="right" colSpan={1}>
                        {order.delivery_fee}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {parseFloat(order.discount) > 0 && status !== "CANCELED" ? (
                    <TableRow>
                      <TableCell align="right" colSpan={3}>
                        Crédito
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={!showCosts ? { visibility: "hidden" } : {}}
                      ></TableCell>
                      <TableCell align="right" colSpan={1}>
                        -{order.discount}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell align="right" colSpan={3}>
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={!showCosts ? { visibility: "hidden" } : {}}
                    >
                      {showCosts ? totalCost.toFixed(2) : null}
                    </TableCell>
                    <TableCell align="right">{total.toFixed(2)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </ListItem>
        </List>
      </Collapse>
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <DialogContentText>
            Você está cancelando um pedido pago. Você deseja gerar crédito para
            a PU? Caso for devolver o dinheiro para a PU, selecione "Não".
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleCancel(false)}>Não</Button>
          <Button onClick={handleCancel(true)} autoFocus>
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Order;
